/**
 * Evaluación única de alertas de pricing/IVA en catalogación (SCRUM-100).
 * Reutiliza commercialMargin/resolveFiscal — no es un segundo motor, es la
 * misma fuente de verdad que ya usa el cron de low_margin (api.cron.stock-alerts.ts).
 * Ninguna regla es bloqueante hoy: el sistema alerta, nunca decide el precio.
 */
import type { PriceMode, TaxCategory } from "./types.js";
import { commercialMargin } from "./margin.js";
import { roundMoney } from "./money.js";

export type PricingAlertSeverity = "warning" | "info";

export type PricingAlertCode =
  | "cost_without_price"
  | "price_equals_cost"
  | "price_below_cost"
  | "markup_inconsistent"
  | "tax_rate_missing";

export interface PricingAlert {
  code: PricingAlertCode;
  severity: PricingAlertSeverity;
  message: string;
}

export interface ProductPricingInput {
  costoReferencia: number | null;
  markupPct: number | null;
  precioUnitario: number | null;
  precioPorKg: number | null;
  tipoVenta: "unidad" | "peso";
  priceMode: PriceMode;
  taxCategory: TaxCategory;
  taxRate: number | null;
}

/** Costo declarado en catalogación se trata FINAL (IVA incluido) — misma convención que sm_purchase_order_items.cost_price. */
const COST_DEFAULT_MODE = "final" as const;

/** Tolerancia para considerar el precio "consistente" con el cálculo de markup (redondeos). */
const MARKUP_TOLERANCE_ABS = 0.5;
const MARKUP_TOLERANCE_REL = 0.02;

export function evaluateProductPricing(product: ProductPricingInput): PricingAlert[] {
  const alerts: PricingAlert[] = [];
  const precioVenta = product.tipoVenta === "peso" ? product.precioPorKg : product.precioUnitario;
  const costo = product.costoReferencia;
  const tieneCosto = costo != null && costo > 0;
  const tienePrecio = precioVenta != null && precioVenta > 0;

  if (tieneCosto && !tienePrecio) {
    alerts.push({
      code: "cost_without_price",
      severity: "warning",
      message:
        "Este producto tiene costo cargado pero no tiene precio de venta definido. Sin un precio final, no podés venderlo con margen conocido.",
    });
  }

  if (tieneCosto && tienePrecio) {
    const margin = commercialMargin({
      saleAmount: precioVenta!,
      saleFiscal: {
        priceMode: product.priceMode,
        taxCategory: product.taxCategory,
        taxRate: product.taxRate,
      },
      costAmount: costo!,
      // El costo declarado en catalogación no tiene alícuota propia — se asume el mismo
      // tratamiento fiscal que el producto (misma categoría/alícuota), no un valor inventado.
      costFiscal: {
        priceMode: COST_DEFAULT_MODE,
        taxCategory: product.taxCategory,
        taxRate: product.taxRate,
      },
      defaultCostMode: COST_DEFAULT_MODE,
    });

    if (margin.comparable) {
      if (margin.marginAmount < 0) {
        alerts.push({
          code: "price_below_cost",
          severity: "warning",
          message: `El precio de venta es menor al costo — estás vendiendo a pérdida (margen ${margin.marginPct}%).`,
        });
      } else if (roundMoney(margin.marginAmount) === 0) {
        alerts.push({
          code: "price_equals_cost",
          severity: "warning",
          message: "El precio de venta es igual al costo: el margen es 0%.",
        });
      }
    }
  }

  if (tieneCosto && product.markupPct != null && tienePrecio) {
    const expected = costo! * (1 + product.markupPct / 100);
    const diff = Math.abs(expected - precioVenta!);
    if (diff > MARKUP_TOLERANCE_ABS && diff / expected > MARKUP_TOLERANCE_REL) {
      alerts.push({
        code: "markup_inconsistent",
        severity: "info",
        message: `Con el markup definido (${product.markupPct}%) el precio sugerido sería $${expected.toFixed(2)}, distinto al precio actual. Es solo informativo — el precio guardado no cambia solo.`,
      });
    }
  }

  if (product.taxCategory === "taxable" && product.taxRate == null) {
    alerts.push({
      code: "tax_rate_missing",
      severity: "info",
      message: "Este producto no tiene alícuota de IVA propia — se va a usar la alícuota general del comercio al facturar.",
    });
  }

  return alerts;
}
